import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import moment from "moment-timezone";
import Flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es.js";

import Header from "../components/Header";
import Footer from "../components/Footer";
import ScrollToTop from "../components/ScrollTop";
import PhoneLoader from "../components/loader/PhoneLoader";

import {
    ResponsiveContainer,
    AreaChart, Area,
    BarChart, Bar,
    LineChart, Line,
    PieChart, Pie, Cell,
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    ScatterChart, Scatter, ZAxis,
    ComposedChart,
    CartesianGrid,
    Tooltip,
    Legend,
    XAxis,
    YAxis
} from "recharts";

import { useAuth } from "../provider/authProvider";
import "../CreditAnalysis.css";

const TZ = "America/Lima";

const TABS = [
    { key: "general", label: "General" },
    { key: "gestores", label: "Gestores (Rol 4)" },
    { key: "creditos", label: "Créditos" },
];

// Si tu branding tiene colores específicos, los puedes cambiar aquí sin tocar lógica
const PIE_COLORS = ["#4F46E5", "#06B6D4", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#64748B"];

function money(n) {
    const v = Number(n || 0);
    try {
        return new Intl.NumberFormat("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
    } catch {
        return v.toFixed(2);
    }
}

function pct(n) {
    const v = Number(n || 0);
    return `${v.toFixed(2)}%`;
}

function num2(n) {
    const v = Number(n || 0);
    return v.toFixed(2);
}


function KPI({ title, value, sub, trend }) {
    return (
        <div className="caKpiCard">
            <div className="caKpiTop">
                <div className="caKpiTitle">{title}</div>
                {trend ? <div className={`caKpiTrend ${trend.type}`}>{trend.text}</div> : null}
            </div>
            <div className="caKpiValue">{value}</div>
            {sub ? <div className="caKpiSub">{sub}</div> : null}
        </div>
    );
}

function TabButton({ active, onClick, children }) {
    return (
        <button className={`caTabBtn ${active ? "active" : ""}`} onClick={onClick}>
            {children}
        </button>
    );
}

export default function CreditAnalysis() {
    const { rol } = useAuth();

    const rangeRef = useRef(null);

    const [tab, setTab] = useState("general");
    const [loading, setLoading] = useState(false);

    // filtros
    const [from, setFrom] = useState(moment().tz(TZ).subtract(90, "days").format("YYYY-MM-DD"));
    const [to, setTo] = useState(moment().tz(TZ).format("YYYY-MM-DD"));
    const [cobranzaUserId, setCobranzaUserId] = useState("");

    // tabla créditos
    const [search, setSearch] = useState("");
    const [minDpd, setMinDpd] = useState("");
    const [page, setPage] = useState(1);
    const [limit] = useState(50);

    const [dashboard, setDashboard] = useState(null);
    const [creditsTable, setCreditsTable] = useState({ rows: [], total: 0, totalPages: 1 });

    // -------------------------
    // Flatpickr (rango)
    // -------------------------
    useEffect(() => {
        const t = setTimeout(() => {
            if (!rangeRef.current) return;
            if (rangeRef.current._flatpickr) rangeRef.current._flatpickr.destroy();

            Flatpickr(rangeRef.current, {
                locale: Spanish,
                mode: "range",
                dateFormat: "Y-m-d",
                defaultDate: [from, to],
                onChange: (selectedDates) => {
                    if (!selectedDates || selectedDates.length < 2) return;
                    const f = moment(selectedDates[0]).tz(TZ).format("YYYY-MM-DD");
                    const tt = moment(selectedDates[1]).tz(TZ).format("YYYY-MM-DD");
                    setFrom(f);
                    setTo(tt);
                    setPage(1);
                },
            });
        }, 120);

        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, []);

    // -------------------------
    // Fetch dashboard
    // -------------------------
    const fetchDashboard = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const url = `${process.env.REACT_APP_API}/credit-analysis/dashboard?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}${cobranzaUserId ? `&cobranzaUserId=${encodeURIComponent(cobranzaUserId)}` : ""}`;

            const resp = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (resp.data?.msg === "success") {
                setDashboard(resp.data.data);
            } else {
                setDashboard(null);
            }
        } catch (e) {
            console.error("[CreditAnalysis] dashboard error:", e);
            setDashboard(null);
        } finally {
            setLoading(false);
        }
    };

    // -------------------------
    // Fetch créditos tabla
    // -------------------------
    const fetchCredits = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");

            const params = new URLSearchParams();
            params.set("page", String(page));
            params.set("limit", String(limit));
            if (search.trim()) params.set("search", search.trim());
            if (cobranzaUserId) params.set("cobranzaUserId", cobranzaUserId);
            if (minDpd !== "") params.set("minDpd", String(minDpd));

            const url = `${process.env.REACT_APP_API}/credit-analysis/credits?${params.toString()}`;

            const resp = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (resp.data?.msg === "success") {
                setCreditsTable(resp.data.data);
            } else {
                setCreditsTable({ rows: [], total: 0, totalPages: 1 });
            }
        } catch (e) {
            console.error("[CreditAnalysis] credits error:", e);
            setCreditsTable({ rows: [], total: 0, totalPages: 1 });
        } finally {
            setLoading(false);
        }
    };

    // Carga inicial + refresco por filtros “macro”
    useEffect(() => {
        fetchDashboard();
        // eslint-disable-next-line
    }, [from, to, cobranzaUserId]);

    // Tabla cuando entras al tab créditos o cambian page/búsqueda
    useEffect(() => {
        if (tab !== "creditos") return;
        fetchCredits();
        // eslint-disable-next-line
    }, [tab, page]);

    // Debounce búsqueda para tabla
    useEffect(() => {
        if (tab !== "creditos") return;
        const t = setTimeout(() => {
            setPage(1);
            fetchCredits();
        }, 350);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [search, cobranzaUserId, minDpd]);

    const overview = dashboard?.overview;
    const collectors = dashboard?.collectors;

    const k = overview?.kpis || {};
    const charts = overview?.charts || {};

    const collectorsRows = collectors?.rows || [];
    const collectorsChart = collectors?.charts?.ranking || [];

    const collectorsOptions = useMemo(() => {
        // opciones desde ranking (ya tiene nombre)
        const opts = collectorsRows
            .filter(r => r.collectorId)
            .map(r => ({ id: String(r.collectorId), name: r.collectorName }));
        // agrego "Sin asignar" si existe
        const hasZero = collectorsRows.some(r => !r.collectorId);
        if (hasZero) opts.unshift({ id: "0", name: "Sin asignar" });
        return opts;
    }, [collectorsRows]);

    // data helpers
    const monthData = charts?.byMonth || [];
    const delinquencyData = charts?.delinquencyBuckets || [];
    const punctualityData = charts?.punctuality || [];
    const fpDaysData = charts?.firstPaymentDays || [];
    const fpVsDueData = charts?.firstPaymentVsDue || [];

    // bubble/scatter: “collected vs outstanding” con tamaño ~ par30Rate
    const bubbleData = useMemo(() => {
        return (collectorsRows || []).map((r) => ({
            name: r.collectorName,
            x: Number(r.collected || 0),
            y: Number(r.outstanding || 0),
            z: Math.max(10, Number(r.par30Rate || 0) * 100), // tamaño burbuja
            par30: Number(r.par30Rate || 0) * 100,
            delin: Number(r.delinquencyRate || 0) * 100,
        }));
    }, [collectorsRows]);

    return (
        <div>
            <>
                <Header />
                <ScrollToTop />

                <section className="sectionBody sectionBodyCredit">
                    <div className="containerCustom containerBody">

                        <div className="caPage">
                            <div className="caHeader">
                                <div className="caTitleBox">
                                    <div className="caTitle">Credit Analysis</div>
                                    <div className="caSubtitle">
                                        Panel de KPIs financieros, mora, retorno y desempeño de cobranza (rol 4).
                                    </div>
                                </div>

                                <div className="caFilters">
                                    <div className="caFilter">
                                        <div className="caLabel">Rango</div>
                                        <input ref={rangeRef} className="caInput" placeholder="Selecciona rango" readOnly />
                                    </div>

                                    <div className="caFilter">
                                        <div className="caLabel">Gestor</div>
                                        <select
                                            className="caInput"
                                            value={cobranzaUserId}
                                            onChange={(e) => {
                                                setCobranzaUserId(e.target.value);
                                                setPage(1);
                                            }}
                                        >
                                            <option value="">Todos</option>
                                            {collectorsOptions.map((o) => (
                                                <option key={o.id} value={o.id}>{o.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="caFilter caFilterMeta">
                                        <div className="caLabel">Tu rol</div>
                                        <div className="caMetaPill">Rol {String(rol || "-")}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Tabs */}
                            <div className="caTabs">
                                {TABS.map((t) => (
                                    <TabButton key={t.key} active={tab === t.key} onClick={() => setTab(t.key)}>
                                        {t.label}
                                    </TabButton>
                                ))}
                            </div>

                            {/* Loader */}
                            {loading ? (
                                <div className="caLoader">
                                    <PhoneLoader />
                                </div>
                            ) : null}

                            {/* TAB: GENERAL */}
                            {tab === "general" && (
                                <div className="caGrid">
                                    {/* KPIs */}
                                    <div className="caSection">
                                        <div className="caSectionHeader">
                                            <div className="caSectionTitle">KPIs generales</div>
                                            <div className="caSectionHint">Inversión, exposición, retorno, mora y recuperación.</div>
                                        </div>

                                        <div className="caKpiGrid">
                                            <KPI title="Créditos" value={String(k.totalCredits ?? 0)} sub={`Activos: ${k.activeCredits ?? 0} · Cerrados: ${k.closedCredits ?? 0}`} />
                                            <KPI title="Inversión" value={`S/ ${money(k.investedTotal)}`} sub="Capital financiado" />
                                            <KPI title="Exposición" value={`S/ ${money(k.exposureTotal)}`} sub="Total por cobrar (incluye interés/comisión)" />
                                            <KPI title="Retorno (cobrado)" value={`S/ ${money(k.collectedTotal)}`} sub="Pagos registrados Payments" />
                                            <KPI title="Saldo por cobrar" value={`S/ ${money(k.outstandingTotal)}`} sub="Exposición - Cobrado" />
                                            <KPI title="ROI" value={pct((Number(k.roi || 0) * 100))} sub="(Cobrado - Inversión) / Inversión" />
                                            <KPI title="Puntualidad" value={pct((Number(k.onTimeRate || 0) * 100))} sub="Pagos a tiempo vs pagos tardíos" />
                                            <KPI title="Mora" value={pct((Number(k.delinquencyRate || 0) * 100))} sub={`${k.delinquentCredits ?? 0} créditos con atraso`} />
                                            <KPI title="PAR30" value={pct((Number(k.par30Rate || 0) * 100))} sub={`Créditos con DPD ≥ 30: ${k.par30 ?? 0}`} />
                                            <KPI title="PAR60" value={pct((Number(k.par60Rate || 0) * 100))} sub={`Créditos con DPD ≥ 60: ${k.par60 ?? 0}`} />
                                            <KPI title="PAR90" value={pct((Number(k.par90Rate || 0) * 100))} sub={`Créditos con DPD ≥ 90: ${k.par90 ?? 0}`} />
                                            <KPI title="Recuperación a la fecha" value={pct((Number(k.recoveryRateToDate || 0) * 100))} sub="Cobrado / Esperado al día (según cronograma)" />
                                        </div>
                                    </div>

                                    {/* Charts Row 1 */}
                                    <div className="caTwoCol">
                                        <div className="caCard">
                                            <div className="caCardHeader">
                                                <div className="caCardTitle">Tendencia mensual (Mixed)</div>
                                                <div className="caCardSub">Créditos, inversión, exposición y cobrado (rango seleccionado).</div>
                                            </div>
                                            <div className="caChart">
                                                <ResponsiveContainer width="100%" height={320}>
                                                    <ComposedChart data={monthData}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="ym" />
                                                        {/* <YAxis /> */}
                                                        <YAxis tickFormatter={(v) => num2(v)} />
                                                        <Tooltip />
                                                        <Legend />
                                                        <Bar dataKey="credits_count" name="Créditos" />
                                                        <Line type="monotone" dataKey="invested" name="Inversión" strokeWidth={2} />
                                                        <Line type="monotone" dataKey="collected" name="Cobrado" strokeWidth={2} />
                                                        <Line type="monotone" dataKey="exposure" name="Exposición" strokeWidth={2} />
                                                    </ComposedChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>

                                        <div className="caCard">
                                            <div className="caCardHeader">
                                                <div className="caCardTitle">Mora por bucket (Donut)</div>
                                                <div className="caCardSub">Distribución por días de atraso (DPD).</div>
                                            </div>
                                            <div className="caChart">
                                                <ResponsiveContainer width="100%" height={320}>
                                                    <PieChart>
                                                        <Tooltip />
                                                        <Legend />
                                                        <Pie
                                                            data={delinquencyData}
                                                            dataKey="count"
                                                            nameKey="bucket"
                                                            innerRadius={70}
                                                            outerRadius={110}
                                                            paddingAngle={2}
                                                        >
                                                            {delinquencyData.map((_, idx) => (
                                                                <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Charts Row 2 */}
                                    <div className="caTwoCol">
                                        <div className="caCard">
                                            <div className="caCardHeader">
                                                <div className="caCardTitle">Puntualidad (Pie)</div>
                                                <div className="caCardSub">Pagos a tiempo vs pagos tardíos (por eventos de pago).</div>
                                            </div>
                                            <div className="caChart">
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <PieChart>
                                                        <Tooltip />
                                                        <Legend />
                                                        <Pie data={punctualityData} dataKey="value" nameKey="name" outerRadius={110}>
                                                            {punctualityData.map((_, idx) => (
                                                                <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                                                            ))}
                                                        </Pie>
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>

                                        <div className="caCard">
                                            <div className="caCardHeader">
                                                <div className="caCardTitle">Tiempo a primera cuota (Bar)</div>
                                                <div className="caCardSub">Días desde contrato hasta primer pago (histograma).</div>
                                            </div>
                                            <div className="caChart">
                                                <ResponsiveContainer width="100%" height={300}>
                                                    <BarChart data={fpDaysData}>
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis dataKey="bucket" />
                                                        {/* <YAxis /> */}
                                                        <YAxis tickFormatter={(v) => num2(v)} />
                                                        <Tooltip />
                                                        <Legend />
                                                        <Bar dataKey="count" name="Créditos" />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Charts Row 3 */}
                                    <div className="caCard">
                                        <div className="caCardHeader">
                                            <div className="caCardTitle">Primera cuota: pago real vs cuota programada (Bar)</div>
                                            <div className="caCardSub">
                                                Comparación usando schedule vs primer pago real (Payments).
                                            </div>
                                        </div>
                                        <div className="caChart">
                                            <ResponsiveContainer width="100%" height={300}>
                                                <BarChart data={fpVsDueData}>
                                                    <CartesianGrid strokeDasharray="3 3" />
                                                    <XAxis dataKey="bucket" />
                                                    {/* <YAxis /> */}
                                                    <YAxis tickFormatter={(v) => `S/ ${num2(v)}`} />
                                                    <Tooltip />
                                                    <Legend />
                                                    <Bar dataKey="count" name="Créditos" />
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* TAB: GESTORES */}
                            {tab === "gestores" && (
                                <div className="caGrid">
                                    <div className="caSection">
                                        <div className="caSectionHeader">
                                            <div className="caSectionTitle">Comparativo por Gestor (rol 4)</div>
                                            <div className="caSectionHint">Identifica quién concentra mayor PAR30, mora y menor recuperación.</div>
                                        </div>

                                        <div className="caTwoCol">
                                            <div className="caCard">
                                                <div className="caCardHeader">
                                                    <div className="caCardTitle">Radar performance (Radar)</div>
                                                    <div className="caCardSub">PAR30, Mora, Puntualidad y Recuperación (top por riesgo).</div>
                                                </div>

                                                <div className="caChart">
                                                    <ResponsiveContainer width="100%" height={360}>
                                                        <RadarChart data={collectorsChart.slice(0, 8)}>
                                                            <PolarGrid />
                                                            <PolarAngleAxis dataKey="name" />
                                                            <PolarRadiusAxis
                                                                angle={30}
                                                                domain={[0, 100]}
                                                                tickFormatter={(v) => `${num2(v)}%`}
                                                            />
                                                            <Tooltip
                                                                formatter={(value, name) => [`${num2(value)}%`, name]}
                                                            />
                                                            <Legend />


                                                            <Legend />
                                                            <Radar name="PAR30%" dataKey="par30Rate" strokeWidth={2} fillOpacity={0.15} />
                                                            <Radar name="Mora%" dataKey="delinquencyRate" strokeWidth={2} fillOpacity={0.10} />
                                                            <Radar name="Puntualidad%" dataKey="onTimeRate" strokeWidth={2} fillOpacity={0.10} />
                                                            <Radar name="Recuperación%" dataKey="recoveryRateToDate" strokeWidth={2} fillOpacity={0.10} />
                                                        </RadarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>

                                            <div className="caCard">
                                                <div className="caCardHeader">
                                                    <div className="caCardTitle">Cartera por gestor (Bar)</div>
                                                    <div className="caCardSub">Cobrado, invertido y saldo por cobrar.</div>
                                                </div>

                                                <div className="caChart">
                                                    <ResponsiveContainer width="100%" height={360}>
                                                        <BarChart data={collectorsChart.slice(0, 12)}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis dataKey="name" tickFormatter={(v) => `S/ ${num2(v)}`} hide />
                                                            {/* <YAxis /> */}
                                                            <YAxis tickFormatter={(v) => `S/ ${num2(v)}`} />
                                                            {/* <Tooltip /> */}
                                                            <Tooltip
                                                                formatter={(value, name) => {
                                                                    if (String(name).toLowerCase().includes("rate") || String(name).includes("%")) {
                                                                        return [`${num2(value)}%`, name];
                                                                    }
                                                                    if (typeof value === "number") {
                                                                        return [num2(value), name];
                                                                    }
                                                                    return [value, name];
                                                                }}
                                                            />

                                                            <Legend />
                                                            <Bar dataKey="invested" name="Inversión" />
                                                            <Bar dataKey="collected" name="Cobrado" />
                                                            <Bar dataKey="outstanding" name="Saldo" />
                                                        </BarChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="caTwoCol">
                                            <div className="caCard">
                                                <div className="caCardHeader">
                                                    <div className="caCardTitle">Bubble/Scatter (Bubble)</div>
                                                    <div className="caCardSub">Cobrado vs saldo; tamaño burbuja ~ PAR30%.</div>
                                                </div>

                                                <div className="caChart">
                                                    <ResponsiveContainer width="100%" height={360}>
                                                        <ScatterChart>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis type="number" dataKey="x" name="Cobrado" tickFormatter={(v) => `S/ ${money(v)}`} />
                                                            <YAxis type="number" dataKey="y" name="Saldo" tickFormatter={(v) => `S/ ${money(v)}`} />
                                                            <ZAxis type="number" dataKey="z" range={[60, 240]} />
                                                            <Tooltip cursor={{ strokeDasharray: "3 3" }} />
                                                            <Legend />
                                                            <Scatter name="Gestores" data={bubbleData} />
                                                        </ScatterChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>

                                            <div className="caCard">
                                                <div className="caCardHeader">
                                                    <div className="caCardTitle">Tabla ranking (comparativo)</div>
                                                    <div className="caCardSub">Ordenado por riesgo (PAR30/Mora).</div>
                                                </div>

                                                <div className="caTableWrap">
                                                    <table className="caTable">
                                                        <thead>
                                                            <tr>
                                                                <th>Gestor</th>
                                                                <th>Créditos</th>
                                                                <th>PAR30%</th>
                                                                <th>Mora%</th>
                                                                <th>Puntualidad%</th>
                                                                <th>Recup.%</th>
                                                                <th>Cobrado</th>
                                                                <th>Saldo</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {collectorsRows.map((r) => (
                                                                <tr key={String(r.collectorId ?? r.collectorName)}>
                                                                    <td className="caTdStrong">{r.collectorName}</td>
                                                                    <td>{r.credits}</td>
                                                                    <td>{pct((Number(r.par30Rate || 0) * 100))}</td>
                                                                    <td>{pct((Number(r.delinquencyRate || 0) * 100))}</td>
                                                                    <td>{pct((Number(r.onTimeRate || 0) * 100))}</td>
                                                                    <td>{pct((Number(r.recoveryRateToDate || 0) * 100))}</td>
                                                                    <td>S/ {money(r.collected)}</td>
                                                                    <td>S/ {money(r.outstanding)}</td>
                                                                </tr>
                                                            ))}
                                                            {!collectorsRows.length ? (
                                                                <tr><td colSpan="8" className="caEmpty">No hay gestores/inputs suficientes para mostrar ranking.</td></tr>
                                                            ) : null}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            )}

                            {/* TAB: CRÉDITOS */}
                            {tab === "creditos" && (
                                <div className="caGrid">
                                    <div className="caSection">
                                        <div className="caSectionHeader">
                                            <div className="caSectionTitle">Listado de créditos</div>
                                            <div className="caSectionHint">Tabla paginada con DPD, saldo, avance y recuperación al día.</div>
                                        </div>

                                        <div className="caTableControls">
                                            <div className="caSearchBox">
                                                <div className="caLabel">Buscar</div>
                                                <input
                                                    className="caInput"
                                                    value={search}
                                                    onChange={(e) => setSearch(e.target.value)}
                                                    placeholder="DNI, nombre, equipo, marca, contrato…"
                                                />
                                            </div>

                                            <div className="caSearchBox">
                                                <div className="caLabel">Min DPD</div>
                                                <input
                                                    className="caInput"
                                                    value={minDpd}
                                                    onChange={(e) => setMinDpd(e.target.value)}
                                                    placeholder="Ej: 30"
                                                />
                                            </div>

                                            <div className="caPager">
                                                <button
                                                    className="caBtn"
                                                    disabled={page <= 1}
                                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                                >
                                                    Anterior
                                                </button>

                                                <div className="caPagerMeta">
                                                    Página <span className="caTdStrong">{page}</span> de{" "}
                                                    <span className="caTdStrong">{creditsTable.totalPages || 1}</span>
                                                    <span className="caPagerSmall"> · Total: {creditsTable.total || 0}</span>
                                                </div>

                                                <button
                                                    className="caBtn"
                                                    disabled={page >= (creditsTable.totalPages || 1)}
                                                    onClick={() => setPage((p) => p + 1)}
                                                >
                                                    Siguiente
                                                </button>
                                            </div>
                                        </div>

                                        <div className="caTableWrap">
                                            <table className="caTable">
                                                <thead>
                                                    <tr>
                                                        <th>ID</th>
                                                        <th>DNI</th>
                                                        <th>Cliente</th>
                                                        <th>Equipo</th>
                                                        <th>Cuotas</th>
                                                        <th>DPD</th>
                                                        <th>Inversión</th>
                                                        <th>Exposición</th>
                                                        <th>Cobrado</th>
                                                        <th>Saldo</th>
                                                        <th>Recup. al día</th>
                                                        <th>Próx. venc.</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(creditsTable.rows || []).map((r) => (
                                                        <tr key={r.fr_id} className={r.dpd > 0 ? "rowRisk" : ""}>
                                                            <td className="caTdStrong">{r.fr_id}</td>
                                                            <td>{r.dni}</td>
                                                            <td>{r.full_name}</td>
                                                            <td>{r.phone_name}</td>
                                                            <td>
                                                                <span className="badge">
                                                                    {r.schedule_paid}/{r.schedule_total}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className={`dpdBadge ${r.dpd > 0 ? "late" : "ok"}`}>
                                                                    {r.dpd} ({r.dpd_bucket})
                                                                </span>
                                                            </td>
                                                            <td>S/ {money(r.invested)}</td>
                                                            <td>S/ {money(r.exposure)}</td>
                                                            <td>S/ {money(r.collected)}</td>
                                                            <td className="caTdStrong">S/ {money(r.outstanding)}</td>
                                                            <td>{r.recovery_rate_to_date == null ? "-" : pct(Number(r.recovery_rate_to_date) * 100)}</td>
                                                            <td>{r.next_due || "-"}</td>
                                                        </tr>
                                                    ))}
                                                    {!creditsTable.rows?.length ? (
                                                        <tr><td colSpan="12" className="caEmpty">Sin resultados.</td></tr>
                                                    ) : null}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Mini chart de apoyo: pagos vs exposición (Line/Area) usando preview si existe */}
                                        <div className="caTwoCol">
                                            <div className="caCard">
                                                <div className="caCardHeader">
                                                    <div className="caCardTitle">
                                                        Distribución por bucket DPD (Days Past Due - días de atraso)
                                                        (Pie - gráfico circular)
                                                    </div>
                                                    <div className="caCardSub">Útil para enfoque operativo en cobranza.</div>
                                                </div>
                                                <div className="caChart">
                                                    <ResponsiveContainer width="100%" height={300}>
                                                        <PieChart>
                                                            <Tooltip />
                                                            <Legend />
                                                            <Pie data={delinquencyData} dataKey="count" nameKey="bucket" outerRadius={110}>
                                                                {delinquencyData.map((_, idx) => (
                                                                    <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                                                                ))}
                                                            </Pie>
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>

                                            <div className="caCard">
                                                <div className="caCardHeader">
                                                    <div className="caCardTitle">Tendencia de retorno (Area)</div>
                                                    <div className="caCardSub">Cobrado mensual (rango seleccionado).</div>
                                                </div>
                                                <div className="caChart">
                                                    <ResponsiveContainer width="100%" height={300}>
                                                        <AreaChart data={monthData}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis dataKey="ym" />
                                                            {/* <YAxis /> */}
                                                            <YAxis tickFormatter={(v) => `S/ ${num2(v)}`} />
                                                            <Tooltip />
                                                            <Legend />
                                                            <Area type="monotone" dataKey="collected" name="Cobrado" strokeWidth={2} fillOpacity={0.25} />
                                                        </AreaChart>
                                                    </ResponsiveContainer>
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            )}
                        </div>

                    </div>
                </section>

                <Footer />
            </>
        </div>
    );
}
